// The six starting stalls behind the start line: timber partitions, a maroon header with lane
// numbers and paw plates, and front doors that swing open when the race starts and close again
// at post time.
import { Group, Mesh, MeshStandardMaterial, PlaneGeometry, type Object3D } from 'three';
import { LANE_COUNT, LANE_WIDTH, laneCenterZ } from '../../data/track';
import type { Phase } from '../../race/types';
import { GATES, TRACK_HALF_WIDTH } from './layout';
import type { EnvMaterials } from './materials';
import { stallHeaders } from './signs';
import { box, merge } from './util';

export class Gates {
  readonly group = new Group();
  private doors: { pivot: Object3D; dir: number }[] = [];
  private open = 0;
  private target = 0;

  constructor(mats: EnvMaterials) {
    this.group.name = 'start-stalls';
    const D = GATES.depth, H = GATES.height;
    const x0 = -D; // back of the stalls; front is at x = 0
    const half = TRACK_HALF_WIDTH;

    // Partitions between stalls and at both ends: planked walls with timber posts.
    const walls = [], posts = [];
    for (let i = 0; i <= LANE_COUNT; i++) {
      const z = -half + i * LANE_WIDTH;
      walls.push(box(D, H, 0.08, { at: [x0 + D / 2, H / 2 + 0.05, z], tile: 1.6, shade: 0.85 }));
      for (const x of [x0, 0]) posts.push(box(0.16, GATES.headerY + 0.3, 0.16, { at: [x, (GATES.headerY + 0.3) / 2, z], tile: 0.8, grain: 'y' }));
    }
    // Back gates (closed) and a roof.
    walls.push(box(0.08, H, half * 2, { at: [x0, H / 2 + 0.05, 0], tile: 1.6, shade: 0.7 }));
    const roof = box(D + 0.7, 0.1, half * 2 + 0.6, { at: [x0 + D / 2, GATES.headerY + 0.42, 0], rot: [0, 0, -0.08], tile: 1.8, shade: 0.8 });
    const header = box(0.14, 0.6, half * 2 + 0.3, { at: [0.02, GATES.headerY, 0], tile: 1.6 });
    const wood = new Mesh(merge(walls), mats.planks);
    const timber = new Mesh(merge([...posts, header]), mats.timber);
    const roofMesh = new Mesh(roof, mats.roof);
    for (const m of [wood, timber, roofMesh]) { m.castShadow = true; m.receiveShadow = true; this.group.add(m); }

    // Header plates: lane number and paw for each stall, facing down the track.
    const plates = stallHeaders();
    const plateMat = new MeshStandardMaterial({ map: plates, roughness: 0.7 });
    for (let i = 0; i < LANE_COUNT; i++) {
      const g = new PlaneGeometry(LANE_WIDTH * 0.62, 0.52);
      const uv = g.getAttribute('uv');
      for (let k = 0; k < uv.count; k++) uv.setX(k, (i + uv.getX(k)) / LANE_COUNT);
      g.rotateY(Math.PI / 2); // face +x
      const m = new Mesh(g, plateMat);
      m.position.set(0.1, GATES.headerY, laneCenterZ(i));
      m.receiveShadow = true;
      this.group.add(m);
    }

    // Front doors: two leaves per stall, maroon with gold-capped frames, hinged on the partitions.
    const leafW = LANE_WIDTH / 2 - 0.06;
    const leafGeo = merge([
      box(0.05, H * 0.92, leafW, { at: [0, H * 0.46 + 0.06, leafW / 2], tile: 1.2 }),
    ]);
    const trimGeo = merge([
      box(0.065, 0.04, leafW, { at: [0, H * 0.92 + 0.06, leafW / 2], tile: 0.8 }),
      box(0.065, 0.04, leafW, { at: [0, H * 0.5, leafW / 2], tile: 0.8 }),
      box(0.065, 0.04, leafW, { at: [0, 0.1, leafW / 2], tile: 0.8 }),
    ]);
    for (let i = 0; i < LANE_COUNT; i++) {
      const zc = laneCenterZ(i);
      for (const dir of [-1, 1]) {
        const pivot = new Group();
        pivot.position.set(0.04, 0, zc + dir * (LANE_WIDTH / 2 - 0.05));
        pivot.rotation.y = dir < 0 ? 0 : Math.PI; // leaves grow towards the stall centre
        const leaf = new Mesh(leafGeo, mats.paintedMaroon);
        const trim = new Mesh(trimGeo, mats.gold);
        for (const m of [leaf, trim]) { m.castShadow = true; m.receiveShadow = true; pivot.add(m); }
        this.group.add(pivot);
        this.doors.push({ pivot, dir });
      }
    }
  }

  /** Doors close for post time and the start, and swing open once the race is running. */
  setPhase(phase: Phase): void {
    this.target = phase === 'running' || phase === 'finished' ? 1 : 0;
  }

  update(dt: number): void {
    // Spring the doors open quickly (they are pushed by the start), close them slowly.
    const rate = this.target > this.open ? 7 : 1.6;
    this.open += (this.target - this.open) * (1 - Math.exp(-rate * dt));
    const a = this.open * 1.75; // radians, swung out towards the track
    for (const d of this.doors) d.pivot.rotation.y = (d.dir < 0 ? 0 : Math.PI) + (d.dir < 0 ? a : -a);
  }
}
